import SectionMessage from '@atlaskit/section-message';
import { Box, Spinner } from '@fuegokit/react';
import { type PropsWithChildren, useEffect, useState } from 'react';

import { getAddonProperty } from '../services/jira';

const MIGRATION_STATUS_PROPERTY = 'migration-status';

type MigrationStatus = 'pending' | 'in_progress' | 'completed' | 'failed';

function isBlocked(status: MigrationStatus): boolean {
  return status !== 'pending' && status !== 'failed';
}

export function MaintenanceGate({ children }: Readonly<PropsWithChildren>) {
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const value = await getAddonProperty<MigrationStatus>(
          MIGRATION_STATUS_PROPERTY,
          'pending'
        );
        const status: MigrationStatus =
          typeof value === 'string' &&
          ['pending', 'in_progress', 'completed', 'failed'].includes(value)
            ? (value as MigrationStatus)
            : 'pending';
        setBlocked(isBlocked(status));
      } catch {
        setBlocked(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{ minHeight: '200px' }}
      >
        <Spinner aria-label="Loading…" size="large" />
      </Box>
    );
  }

  if (blocked) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{ minHeight: '200px', padding: '32px' }}
      >
        <SectionMessage appearance="information" title="Scheduled maintenance">
          <p>
            Toggl for Jira is currently undergoing scheduled maintenance and is
            temporarily unavailable. This process typically takes around 15
            minutes.
          </p>
          <p>Please check back shortly. We apologize for the inconvenience.</p>
        </SectionMessage>
      </Box>
    );
  }

  return <>{children}</>;
}
